import {useQuery} from '@tanstack/react-query';
import {config} from '../config';
import {useBankIDClient} from './useBankIDClient';

export interface BankIDCodeRequest {
  type: string | undefined | null;
  token: string | undefined | null;
  authToken: string | undefined | null;
  bft?: string | undefined | null;
  host?: string;
  // Set to false once a caller already has a QR code from another source
  // (e.g. merged into the status poll, or BankID already launched on this
  // device), so this legacy poll can stop.
  enabled?: boolean;
  refreshAuthToken?: () => Promise<string | undefined>;
}

export const useBankIDCode = ({
  token,
  authToken,
  bft,
  host,
  enabled = true,
  refreshAuthToken,
}: BankIDCodeRequest) => {
  const client = useBankIDClient(authToken ?? undefined, refreshAuthToken);
  const bankIdHost = host ?? config.bankIdHost;
  const isEnabled =
    enabled && Boolean(token) && Boolean(authToken) && Boolean(bft);

  const query = useQuery({
    queryKey: ['bankid', 'qr', token ?? 'none', bft ?? 'none'] as const,
    enabled: isEnabled,
    //refetchInterval: isEnabled ? 2000 : false,
    refetchInterval: isEnabled
      ? (query: {state: {dataUpdatedAt: number}}) => {
          return query.state.dataUpdatedAt === 0 ? 1000 : 2000;
        }
      : false,
    queryFn: async () => {
      if (!token) throw new Error('Token required for BankID QR code');
      if (!bft) throw new Error('BFT required for BankID QR code');
      const url = `${bankIdHost}/authentication/${token}/qr?bft=${encodeURIComponent(
        bft,
      )}`;
      return await client.getJSONBody<{
        id?: string;
        qrCode?: string;
      }>(url);
    },
  });

  return {
    query,
  };
};
